import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomerPortalSessionData,
  IPaymentMethodContactSchema,
} from '@monetize/types/app';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  useCreateCustomerPortalPaymentMethod,
  useGenerateCustomerPortalPaymentMethodToken,
} from '../../../../api/customerPortalService';
import {
  MBox,
  MButton,
  MCenterModal,
  MCheckbox,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MStack,
} from '../../../../components/Monetize';
import { StripeWrapper } from '../../../../components/PaymentMethods/Form/StripeWrapper';
import { useToast } from '../../../../services/toast';

const NewPaymentMethodSchema = z.object({
  paymentMethodName: z
    .string()
    .trim()
    .min(1, 'Payment method name is required'),
  setAsDefault: z.boolean(),
  billingDetails: z.object({
    fullName: z.string().trim().min(1, 'Full name is required'),
    email: z
      .string()
      .trim()
      .refine((value) => !value || z.email().safeParse(value).success, {
        message: 'Enter a valid email address',
      }),
    phone: z.string().trim().optional(),
    title: z.string().trim().optional(),
    line1: z.string().trim().optional(),
    line2: z.string().trim().optional(),
    city: z.string().trim().optional(),
    state: z.string().trim().optional(),
    postalCode: z.string().trim().optional(),
    country: z.string().trim().optional(),
  }),
});

type NewPaymentMethodFormValues = z.infer<typeof NewPaymentMethodSchema>;

interface StripeSectionProps {
  onStripeReadyChange: (ready: boolean) => void;
}

const StripeSection = ({ onStripeReadyChange }: StripeSectionProps) => {
  const onChange = ({ complete }: StripePaymentElementChangeEvent) => {
    onStripeReadyChange(complete);
  };

  return (
    <PaymentElement
      onChange={onChange}
      options={{
        layout: {
          type: 'accordion',
          radios: true,
          defaultCollapsed: false,
          spacedAccordionItems: true,
        },
        terms: {
          usBankAccount: 'never',
          card: 'never',
        },
        fields: {
          billingDetails: {
            name: 'never',
            email: 'never',
            phone: 'auto',
            address: 'auto',
          },
        },
      }}
    />
  );
};

interface NewPaymentMethodModalContentProps {
  sessionData: CustomerPortalSessionData;
  paymentGatewayId: string;
  defaultBillingDetails?: Partial<IPaymentMethodContactSchema>;
  canChangeDefault: boolean;
  onClose: () => void;
}

const NewPaymentMethodModalContent = ({
  sessionData,
  paymentGatewayId,
  defaultBillingDetails,
  canChangeDefault,
  onClose,
}: NewPaymentMethodModalContentProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isStripeReady, setIsStripeReady] = useState(false);
  const [saveError, setSaveError] = useState('');

  const formState = useForm<NewPaymentMethodFormValues>({
    resolver: zodResolver(NewPaymentMethodSchema),
    mode: 'onSubmit',
    defaultValues: {
      paymentMethodName: '',
      setAsDefault: true,
      billingDetails: {
        fullName: defaultBillingDetails?.fullName || '',
        email: defaultBillingDetails?.email || '',
        phone: defaultBillingDetails?.phone || '',
        title: defaultBillingDetails?.title || '',
        line1: defaultBillingDetails?.line1 || '',
        line2: defaultBillingDetails?.line2 || '',
        city: defaultBillingDetails?.city || '',
        state: defaultBillingDetails?.state || '',
        postalCode: defaultBillingDetails?.postalCode || '',
        country: defaultBillingDetails?.country || '',
      },
    },
  });

  const { mutateAsync: createPaymentMethod, isPending } =
    useCreateCustomerPortalPaymentMethod({
      meta: { showErrorToast: true },
    });

  const submit = async (values: NewPaymentMethodFormValues) => {
    setSaveError('');
    if (!stripe || !elements) {
      return;
    }

    const elementValidation = await elements.submit();
    if (elementValidation.error) {
      setSaveError(
        elementValidation.error.message || 'Invalid payment details',
      );
      return;
    }

    const { billingDetails } = values;
    const confirmation = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
        payment_method_data: {
          billing_details: {
            email: billingDetails.email,
            name: billingDetails.fullName,
            phone: billingDetails.phone,
          },
        },
      },
      redirect: 'if_required',
    });

    if (confirmation.error) {
      setSaveError(
        confirmation.error.message ||
          'Unable to verify payment method. Please try again.',
      );
      return;
    }

    if (!confirmation.setupIntent?.id) {
      setSaveError('Unable to verify payment method. Please try again.');
      return;
    }

    if (
      confirmation.setupIntent.status !== 'succeeded' &&
      confirmation.setupIntent.status !== 'processing'
    ) {
      setSaveError(
        'Payment method verification is not complete yet. Please try again.',
      );
      return;
    }

    await createPaymentMethod({
      sessionData,
      payload: {
        paymentGatewayId,
        paymentMethod: {
          paymentToken: confirmation.setupIntent.id,
          paymentMethodName: values.paymentMethodName,
          amount: 0,
          description: '',
          setAsDefault: canChangeDefault && values.setAsDefault,
          billingDetails: {
            ...values.billingDetails,
            email: values.billingDetails.email || undefined,
          },
        },
      },
    });
    onClose();
  };

  const { control, handleSubmit, formState: state } = formState;

  return (
    <MCenterModal
      isOpen
      onClose={onClose}
      modalTitle="Add Payment Method"
      size="xl"
      closeOnOverlayClick={!isPending}
      closeOnEsc={!isPending}
      renderFooter={() => (
        <MStack direction="row" gap={3}>
          <MButton onClick={onClose} variant="cancel" isDisabled={isPending}>
            Cancel
          </MButton>
          <MButton
            variant="primary"
            onClick={handleSubmit(submit)}
            isLoading={isPending}
            isDisabled={!isStripeReady || isPending}
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
        <MGridItem colSpan={12}>
          <StripeSection onStripeReadyChange={setIsStripeReady} />
        </MGridItem>
        <MGridItem colSpan={{ base: 12, md: 6 }}>
          <MFormField
            label="Payment Method Name"
            isRequired
            error={state.errors.paymentMethodName}
          >
            <Controller
              control={control}
              name="paymentMethodName"
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={{ base: 12, md: 6 }}>
          <MFormField
            label="Full Name"
            isRequired
            error={state.errors.billingDetails?.fullName}
          >
            <Controller
              control={control}
              name="billingDetails.fullName"
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={{ base: 12, md: 6 }}>
          <MFormField label="Email" error={state.errors.billingDetails?.email}>
            <Controller
              control={control}
              name="billingDetails.email"
              render={({ field }) => <MInput {...field} type="email" />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={{ base: 12, md: 6 }}>
          <MFormField label="Phone">
            <Controller
              control={control}
              name="billingDetails.phone"
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        {canChangeDefault && (
          <MGridItem colSpan={12}>
            <Controller
              control={control}
              name="setAsDefault"
              render={({ field: { value, onChange, ...rest } }) => (
                <MCheckbox
                  isChecked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  {...rest}
                >
                  Set as default payment method
                </MCheckbox>
              )}
            />
          </MGridItem>
        )}
      </MGrid>
      {!!saveError && (
        <MBox mt={3} color="tRed.base" fontSize="sm">
          {saveError}
        </MBox>
      )}
    </MCenterModal>
  );
};

interface NewPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionData: CustomerPortalSessionData;
  paymentGatewayId?: string;
  defaultBillingDetails?: Partial<IPaymentMethodContactSchema>;
  canChangeDefault: boolean;
}

export const NewPaymentMethodModal = ({
  isOpen,
  onClose,
  sessionData,
  paymentGatewayId,
  defaultBillingDetails,
  canChangeDefault,
}: NewPaymentMethodModalProps) => {
  const [clientSecret, setClientSecret] = useState<string>();
  const [stripePublicKey, setStripePublicKey] = useState<string>();
  const { addToast } = useToast();

  const {
    mutateAsync: generateToken,
    isPending,
    reset,
  } = useGenerateCustomerPortalPaymentMethodToken({
    onError: () => {
      addToast({
        severity: 'error',
        summary: 'Unable to initialize payment method',
        detail: 'Please try again.',
      });
      onClose();
    },
  });

  const isReady = useMemo(
    () => !!paymentGatewayId && !!stripePublicKey && !!clientSecret,
    [clientSecret, paymentGatewayId, stripePublicKey],
  );

  useEffect(() => {
    if (!isOpen || !paymentGatewayId || clientSecret) {
      return;
    }
    generateToken({
      sessionData,
      payload: { paymentGatewayId },
    })
      .then((response) => {
        setClientSecret(response.paymentGateway.clientSecret);
        setStripePublicKey(response.paymentGateway.publicKey);
      })
      .catch(() => {});
  }, [
    clientSecret,
    generateToken,
    isOpen,
    onClose,
    paymentGatewayId,
    sessionData,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setClientSecret(undefined);
      setStripePublicKey(undefined);
      reset();
    }
  }, [isOpen, reset]);

  if (!isOpen) {
    return null;
  }

  if (isPending || !isReady || !paymentGatewayId || !stripePublicKey) {
    return (
      <MCenterModal
        isOpen
        onClose={onClose}
        modalTitle="Add Payment Method"
        isLoading
      >
        Loading...
      </MCenterModal>
    );
  }

  if (!stripePublicKey || !clientSecret) {
    return null;
  }

  return (
    <StripeWrapper apiKey={stripePublicKey} clientSecret={clientSecret}>
      <NewPaymentMethodModalContent
        sessionData={sessionData}
        paymentGatewayId={paymentGatewayId}
        defaultBillingDetails={defaultBillingDetails}
        canChangeDefault={canChangeDefault}
        onClose={onClose}
      />
    </StripeWrapper>
  );
};
